import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { eur } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import NextStepsBlock from "@/components/NextStepsBlock";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Ruler, Home as HomeIcon, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";

function ListingGallery({ images, alt }) {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) {
    return <div className="aspect-[16/10] bg-navy/5 mb-8 flex items-center justify-center text-navy/40">Aucune photo</div>;
  }
  const total = images.length;
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);
  return (
    <div className="mb-8" data-testid="listing-gallery">
      <div className="relative aspect-[16/10] bg-navy/5 overflow-hidden">
        <img src={images[idx]} alt={`${alt} — photo ${idx + 1}`} width="1200" height="750" decoding="async" className="w-full h-full object-cover" />
        {total > 1 && (
          <>
            <button type="button" onClick={prev} aria-label="Photo précédente" data-testid="gallery-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 shadow">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={next} aria-label="Photo suivante" data-testid="gallery-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 shadow">
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-3 right-3 bg-navy/80 text-cream text-xs px-2 py-1 font-mono">{idx + 1} / {total}</div>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 mt-3">
          {images.map((src, i) => (
            <button key={i} type="button" onClick={() => setIdx(i)} data-testid={`gallery-thumb-${i}`}
              className={`aspect-[4/3] overflow-hidden border-2 ${i === idx ? "border-accent-brand" : "border-transparent hover:border-navy/30"}`}>
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function AnnonceDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "Je souhaite recevoir le dossier complet de cette annonce." });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setL(data)).catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", { listing_id: id, ...form });
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally { setSubmitting(false); }
  };

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-navy/60">Annonce introuvable. <Link to="/annonces" className="underline">Retour aux annonces</Link></div>;
  if (!l) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-navy/60">Chargement...</div>;

  return (
    <>
      <SEO title={`${l.title} — ${l.city} — venteaterme.fr`} description={`Vente à terme ${l.property_type} à ${l.city}. Bouquet ${eur(l.bouquet)}, mensualité ${eur(l.monthly_payment)} sur ${l.duration_years} ans.`} />

      <section className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Breadcrumbs items={[
            { href: "/", label: "Accueil" },
            { href: "/annonces", label: "Annonces" },
            { href: `/annonces/${l.id}`, label: l.title },
          ]} />
          <Link to="/annonces" data-testid="back-to-list" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-accent-brand mb-6">
            <ArrowLeft size={16} /> Retour aux annonces
          </Link>

          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10">
            <div>
              <ListingGallery images={l.images || []} alt={l.title} />

              <div className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 mb-4 mr-2 ${l.property_type === "libre" ? "bg-accent-brand text-white" : "bg-navy text-cream"}`}>
                {l.property_type === "libre" ? "Vente à terme libre" : "Vente à terme occupée"}
              </div>
              {l.is_demo && (
                <div className="inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 mb-4 bg-white text-navy border border-navy/20">Annonce de démonstration</div>
              )}

              <h1 className="font-serif text-3xl md:text-5xl text-navy leading-tight mb-4">{l.title}</h1>
              <div className="flex flex-wrap items-center gap-5 text-navy/70 text-sm mb-8">
                <span className="flex items-center gap-1.5"><MapPin size={15} />{l.city}, {l.postal_code}</span>
                <span className="flex items-center gap-1.5"><Ruler size={15} />{l.surface} m²</span>
                <span className="flex items-center gap-1.5"><HomeIcon size={15} />{l.rooms} pièces · {l.bedrooms} ch.</span>
              </div>

              <div className="prose-vt max-w-none mb-10 text-navy/80 leading-relaxed">
                <p>{l.description}</p>
              </div>

              {l.features?.length > 0 && (
                <div className="border-t border-navy/10 pt-8">
                  <h3 className="font-serif text-xl text-navy mb-4">Caractéristiques</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {l.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-navy/75"><CheckCircle2 size={16} className="text-accent-brand" /> {f}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-navy/10 pt-8 mt-8">
                <h3 className="font-serif text-xl text-navy mb-4">Sécurité juridique</h3>
                {l.is_verified && (
                  <div className="mb-4 p-3 bg-accent-brand/10 border-l-4 border-accent-brand flex items-center gap-2" data-testid="detail-verified-badge">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" className="text-accent-brand shrink-0" aria-hidden="true"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <div className="text-sm text-navy"><strong>Bien vérifié</strong> — justificatif de propriété transmis et validé par notre équipe.</div>
                  </div>
                )}
                <ul className="space-y-3 text-sm text-navy/75">
                  <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-brand shrink-0 mt-0.5" /><span>Signature <strong>chez notaire</strong> — acte authentique.</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-brand shrink-0 mt-0.5" /><span><strong>Hypothèque légale spéciale (art. 2402 du Code civil)</strong> — garantie hypothécaire de premier rang inscrite au fichier immobilier.</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-brand shrink-0 mt-0.5" /><span><strong>Clause résolutoire</strong> — récupération du bien en cas d'impayé.</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-brand shrink-0 mt-0.5" /><span><strong>Prix ferme, mensualités fixes, sans intérêts, sans indexation.</strong></span></li>
                </ul>
              </div>

              {(l.dpe_energy || l.dpe_climate) && (
                <div className="border-t border-navy/10 pt-8 mt-8">
                  <h3 className="font-serif text-xl text-navy mb-4">Performance énergétique (DPE)</h3>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    {l.dpe_energy && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-navy/50 mb-1">Classe énergie</div>
                        <div className={`inline-block px-4 py-2 font-serif text-2xl font-bold ${["F","G"].includes(l.dpe_energy) ? "bg-[#c0392b] text-white" : "bg-accent-brand/15 text-navy"}`}>{l.dpe_energy}</div>
                      </div>
                    )}
                    {l.dpe_climate && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-navy/50 mb-1">Classe climat</div>
                        <div className={`inline-block px-4 py-2 font-serif text-2xl font-bold ${["F","G"].includes(l.dpe_climate) ? "bg-[#c0392b] text-white" : "bg-accent-brand/15 text-navy"}`}>{l.dpe_climate}</div>
                      </div>
                    )}
                    {l.dpe_kwh && (
                      <div className="text-sm text-navy/75"><strong>{l.dpe_kwh}</strong> kWh/m²/an</div>
                    )}
                    {l.dpe_costs && (
                      <div className="text-sm text-navy/75">Coûts estimés&nbsp;: <strong>{l.dpe_costs}</strong> €/an</div>
                    )}
                  </div>
                  {(["F","G"].includes(l.dpe_energy)) && (
                    <div className="mt-4 p-4 bg-[#fdf2e9] border-l-4 border-[#c0392b] text-sm text-navy">
                      <strong>Logement à consommation énergétique excessive.</strong> Depuis le 1er janvier 2025, les logements classés G ne peuvent plus être proposés à la location (interdiction progressive pour F en 2028 et E en 2034).
                    </div>
                  )}
                </div>
              )}

              <NextStepsBlock />
            </div>

            <aside className="lg:sticky lg:top-24 self-start">
              <div className="bg-white border border-navy/10 p-8">
                <div className="text-xs uppercase tracking-[0.2em] text-accent-brand font-bold mb-2">Conditions</div>
                <div className="text-sm text-navy/60 mb-6">Valeur retenue : <span className="text-navy font-semibold">{eur(l.market_value)}</span></div>
                <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-navy/10">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/50 mb-1">Bouquet</div>
                    <div className="font-serif text-xl text-accent-brand font-semibold">{eur(l.bouquet)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/50 mb-1">Mensualité</div>
                    <div className="font-serif text-xl text-navy font-semibold">{eur(l.monthly_payment)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/50 mb-1">Durée</div>
                    <div className="font-serif text-xl text-navy font-semibold">{l.duration_years} ans</div>
                  </div>
                </div>

                <h3 className="font-serif text-xl text-navy mb-4">Ce bien m'intéresse — recevez toutes les informations</h3>
                <form onSubmit={submit} className="space-y-3" data-testid="request-dossier-form">
                  <Input data-testid="dossier-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Votre nom" className="bg-white border-navy/20" />
                  <Input data-testid="dossier-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Votre email" className="bg-white border-navy/20" />
                  <Input data-testid="dossier-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (optionnel)" className="bg-white border-navy/20" />
                  <Textarea data-testid="dossier-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Votre message" className="bg-white border-navy/20" />
                  <button disabled={submitting} data-testid="dossier-submit"
                    className="w-full bg-accent-brand text-white font-bold py-3 tracking-wide hover:bg-[#B08D22] transition-colors disabled:opacity-60">
                    {submitting ? "Envoi..." : "Recevoir le dossier"}
                  </button>
                  <p className="text-[11px] text-navy/50 leading-snug">Vos données sont utilisées uniquement pour vous recontacter. Voir mentions RGPD sur la page contact.</p>
                </form>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
